// based on kalidokit: https://github.com/yeemachine/kalidokit/blob/main/src/utils/vector.ts

/** Interface for objects that can be converted to a Vector */
interface VectorLike {
  x: number;
  y: number;
  z: number;
}

/** Type for values that can be converted to a Vector */
type VectorInput = Vector | number | [number, number, number] | VectorLike

/** Vector Math class. */
class Vector {
  public x: number
  public y: number
  public z: number

  constructor (x?: number, y?: number, z?: number) {
    this.x = x || 0
    this.y = y || 0
    this.z = z || 0
  }

  static toVector (value: VectorInput): Vector {
    if (value instanceof Vector) {
      return value
    }

    if (typeof value === 'number') {
      return new Vector(value, value, value)
    }

    if (Array.isArray(value)) {
      return new Vector(value[0], value[1], value[2])
    }

    if (value.x !== undefined && value.y !== undefined && value.z !== undefined) {
      return new Vector(value.x, value.y, value.z)
    }

    throw new Error('Invalid vector value')
  }

  toString (): string {
    return `Vector(${this.x}, ${this.y}, ${this.z})`
  }

  /**
   * Array representation of the vector.
   * @returns {number[]} Array
   * @example
   * new Vector(1, 2, 3).toArray(); // [1, 2, 3]
   */
  toArray (): [number, number, number] {
    return [this.x, this.y, this.z]
  }

  /**
   * Clone the vector.
   * @returns {Vector} New vector
   */
  clone (): Vector {
    return new Vector(this.x, this.y, this.z)
  }

  /**
   * Set this Vector with explicit values
   * @param {VectorInput} v: Vector to set
   */
  set (v: VectorInput): Vector {
    const vector = Vector.toVector(v)
    this.x = vector.x
    this.y = vector.y
    this.z = vector.z

    return this
  }

  /**
   * Returns the negative of this vector.
   */
  neg_inplace (): Vector {
    this.x = -this.x
    this.y = -this.y
    this.z = -this.z

    return this
  }

  /**
   * Returns the negative of this vector.
   */
  neg (): Vector {
    return this.clone().neg_inplace()
  }

  /**
   * Add a vector or number to this vector.
   * @param {VectorInput} v: Vector or number to add
   * @returns {Vector} New vector
   */
  add_inplace (v: VectorInput): Vector {
    const vector = Vector.toVector(v)
    this.x += vector.x
    this.y += vector.y
    this.z += vector.z

    return this
  }

  /**
   * Add a vector or number to this vector.
   * @param {VectorInput} v: Vector or number to add
   * @returns {Vector} New vector
   */
  add (v: VectorInput): Vector {
    return this.clone().add_inplace(v)
  }

  /**
   * Substracts a vector or number from this vector.
   * @param {VectorInput} v: Vector or number to subtract
   * @returns {Vector} New vector
   */
  sub_inplace (v: VectorInput): Vector {
    const vector = Vector.toVector(v)
    this.x -= vector.x
    this.y -= vector.y
    this.z -= vector.z

    return this
  }

  /**
   * Substracts a vector or number from this vector.
   * @param {VectorInput} v: Vector or number to subtract
   * @returns {Vector} New vector
   */
  sub (v: VectorInput): Vector {
    return this.clone().sub_inplace(v)
  }

  /**
   * Multiplies a vector or a number to a vector.
   * @param {VectorInput} v: Vector or number to multiply
   */
  mul_inplace (v: VectorInput): Vector {
    const vector = Vector.toVector(v)
    this.x *= vector.x
    this.y *= vector.y
    this.z *= vector.z

    return this
  }

  /**
   * Multiplies a vector or a number to a vector.
   * @param {VectorInput} v: Vector or number to multiply
   */
  mul (v: VectorInput): Vector {
    return this.clone().mul_inplace(v)
  }

  /**
   * Divide this vector by a vector or a number.
   * @param {VectorInput} v: Vector or number to divide
   * @returns {Vector} New vector
   */
  div_inplace (v: VectorInput): Vector {
    const vector = Vector.toVector(v)
    this.x /= vector.x
    this.y /= vector.y
    this.z /= vector.z

    return this
  }

  /**
   * Divide this vector by a vector or a number.
   * @param {VectorInput} v: Vector or number to divide
   * @returns {Vector} New vector
   */
  div (v: VectorInput): Vector {
    return this.clone().div_inplace(v)
  }

  /**
   * Returns the dot product of this vector and another vector.
   * @param {Vector} v: Vector to dot
   * @returns {number} Dot product
   */
  dot (v: Vector): number {
    return this.x * v.x + this.y * v.y + this.z * v.z
  }

  /**
   * Cross product of two vectors.
   * @param {Vector} v: Vector to cross
   */
  cross_inplace (v: Vector): Vector {
    const x = this.y * v.z - this.z * v.y
    const y = this.z * v.x - this.x * v.z
    const z = this.x * v.y - this.y * v.x
    this.x = x
    this.y = y
    this.z = z

    return this
  }

  /**
   * Cross product of two vectors.
   * @param {Vector} v: Vector to cross
   */
  cross (v: Vector): Vector {
    return this.clone().cross_inplace(v)
  }

  /**
   * Get the length of the Vector
   * @returns {number} Length
   */
  length (): number {
    return Math.sqrt(this.dot(this))
  }

  /**
   * Find the distance between this and another vector.
   * @param {Vector} v: Vector to find distance to
   * @returns {number} Distance
   */
  dist (v: Vector): number {
    return Math.sqrt(Math.pow(this.x - v.x, 2) + Math.pow(this.y - v.y, 2) + Math.pow(this.z - v.z, 2))
  }

  /**
   * Lerp between this vector and another vector.
   * @param {Vector} v: Vector to lerp to
   * @param {number} fraction: Fraction to lerp
   * @returns {Vector}
   */
  lerp_inplace (v: Vector, fraction: number): Vector {
    return this.add_inplace(v.sub(this).mul_inplace(fraction))
  }

  /**
   * Lerp between this vector and another vector.
   * @param {Vector} v: Vector to lerp to
   * @param {number} fraction: Fraction to lerp
   * @returns {Vector}
   */
  lerp (v: Vector, fraction: number): Vector {
    return this.clone().lerp_inplace(v, fraction)
  }

  /**
   * Returns the normalized vector.
   * @returns {Vector} Normalized vector
   */
  norm_inplace (): Vector {
    return this.div_inplace(this.length())
  }

  /**
   * Returns the normalized vector.
   * @returns {Vector} Normalized vector
   */
  norm (): Vector {
    return this.clone().norm_inplace()
  }

  /**
   * Returns the angle between this vector and vector a in radians.
   * @param {VectorInput} v: Vector
   * @returns {number}
   */
  angleTo (v: VectorInput): number {
    const vector = Vector.toVector(v)

    return Math.acos(this.dot(vector) / (this.length() * vector.length()))
  }

  /**
   * Rotate this vector around an axis.
   * @param {Vector} axis: Axis to rotate around
   * @param {number} angle: Angle to rotate
   * @param {Vector} origin: Origin of the rotation
   * @returns {Vector}
   */
  rotateAround_inplace (axis: Vector, angle: number, origin?: Vector): Vector {
    // Translate point to origin
    if (origin) {
      this.sub_inplace(origin)
    }

    // Normalize axis
    const mag = axis.length()
    const normalizedAxis = axis.div(mag)

    // Rodrigues' rotation formula
    const dot = this.dot(normalizedAxis)
    const cosA = Math.cos(angle)
    const sinA = Math.sin(angle)
    const cross = normalizedAxis.cross(this)

    this.mul_inplace(cosA)
    this.add_inplace(cross.mul(sinA))
    this.add_inplace(normalizedAxis.mul(dot * (1 - cosA)))

    // Translate back
    if (origin) {
      this.add_inplace(origin)
    }

    return this
  }

  /**
   * Rotate this vector around an axis.
   * @param {Vector} axis: Axis to rotate around
   * @param {number} angle: Angle to rotate
   * @param {Vector} origin: Origin of the rotation
   * @returns {Vector}
   */
  rotateAround (axis: Vector, angle: number, origin?: Vector): Vector {
    return this.clone().rotateAround_inplace(axis, angle, origin)
  }
}

export default Vector
